import { Injectable } from '@angular/core';
import { FormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { AuthService } from 'src/app/auth/auth.service';

@Injectable()
export class UtilValidatorsService {

  constructor(
    private authService: AuthService
  ) { }

  public emailPattern = /^([a-zA-Z0-9_\.\-])+\@(([a-zA-Z0-9\-])+\.)+([a-zA-Z0-9]{2,4})+$/;

  public postalCodePTPattern = /^[0-9]{4}-[0-9]{3}$/;
  public alphaNumericPattern = /^[a-zA-Z0-9]+$/;

  public validateCUPS = (control: FormControl): ValidationErrors | null => {
    if (control.value === '' && control.touched) {
      control.reset();
      return { required: 'required' };
    }
    const cups = control.value;
    let ret = false;
    let countryCUPS: string;
    if (this.authService.userData.data.country === 'PT') {
      countryCUPS = 'PT';
    } else {
      countryCUPS = '[A-Z]{2}';
    }
    const reCUPS = new RegExp('^' + countryCUPS + '(\\d{4}\\d{12})([A-Z]{2})(\\d[FPCRXYZ])?$');
    if (reCUPS.test(cups)) {
      const mCUPS = cups.toUpperCase().match(reCUPS);
      const cups16 = mCUPS[ 1 ];
      const cupsControl = mCUPS[ 2 ];
      const chars = [
        'T',
        'R',
        'W',
        'A',
        'G',
        'M',
        'Y',
        'F',
        'P',
        'D',
        'X',
        'B',
        'N',
        'J',
        'Z',
        'S',
        'Q',
        'V',
        'H',
        'L',
        'C',
        'K',
        'E'
      ];

      const cup16Mod = parseInt(cups16, 10) % 529;
      const quotient = Math.floor(cup16Mod / 23);
      const remainder = cup16Mod % 23;

      ret = cupsControl === chars[ quotient ] + chars[ remainder ];
    }
    return ret ? null : { invalid_cups: 'invalid_cups' };
  }

  public validateDNI = (control: FormControl): ValidationErrors | null => {
    if (this.authService.userData.data.country === 'PT') {
      return this.validateNIFPT(control);
    } else {
      return this.validateDNIES(control);
    }
  }

  public validateDNIandNIF = (control: FormControl): ValidationErrors | null => {
    return this.validateNIFPT(control) === null || this.validateDNIES(control) === null ? null : { invalid_dni: true };
  }

  public validateNIFPT = (control: FormControl): ValidationErrors | null => {
    const regExpNipc = /^\d{9}$/;
    const nipc = control.value;
    if (!nipc) {
      return null;
    }
    if (regExpNipc.test(nipc)) {
      const firstCharacter = nipc.charAt(0);
      if (
        firstCharacter === '1' ||
        firstCharacter === '2' ||
        firstCharacter === '3' ||
        firstCharacter === '4' ||
        firstCharacter === '8' ||
        firstCharacter === '9'
      ) {
        let checkDigit = firstCharacter * 9;
        for (let i = 2; i <= 8; i++) {
          checkDigit += nipc.charAt(i - 1) * (10 - i);
        }
        checkDigit = (11 - (checkDigit % 11));
        if (checkDigit >= 10) {
          checkDigit = 0;
        }
        if (checkDigit.toString() === nipc.charAt(8)) {
          return null;
        }
      }
    }
    return { invalid_dni: true };
  }

  public validateDNIES = (control: FormControl): ValidationErrors | null => {
    let dni = control.value;
    if (!dni) {
      return null;
    }
    let numero: string;
    let letr: string;
    let letra: string;
    const regExpDNI = /^[XYZ]?\d{5,8}[A-Z]$/;

    dni = dni.toUpperCase();

    if (regExpDNI.test(dni) === true) {
      numero = dni.substr(0, dni.length - 1);
      numero = numero.replace('X', '0');
      numero = numero.replace('Y', '1');
      numero = numero.replace('Z', '2');
      letr = dni.substr(dni.length - 1, 1);
      numero = (parseInt(numero, 10) % 23).toString();
      letra = 'TRWAGMYFPDXBNJZSQVHLCKET';
      letra = letra.substring(parseInt(numero, 10), parseInt(numero, 10) + 1);
      if (letra !== letr) {
        return { invalid_dni: true };
      } else {
        return null;
      }
    } else if (dni === '') {
      return null;
    } else {
      return { invalid_dni: true };
    }
  }

  public validateCIFNIF = (control: FormControl): ValidationErrors | null => {
    let id = control.value;
    if (id) {
      if (this.authService.userData.data.country === 'PT') {
        const niftValid = this.validateNIFPT(control);
        const nipcValid = this.validateNIPC(control);
        if ((!niftValid && !nipcValid) || (niftValid && !nipcValid)) {
          return null;
        } else if (nipcValid) {
          return nipcValid;
        } else {
          return niftValid;
        }
      } else {
        const regExpDNI = /^[XYZ]?\d{5,8}[A-Z]$/;
        id = id.toUpperCase();
        if (regExpDNI.test(id) === true) {
          return this.validateDNIES(control);
        } else {
          return this.validateCif(control);
        }
      }
    } else {
      return null;
    }
  }

  public validateNIPC = (control: FormControl): ValidationErrors | null => {
    const regExpNipc = /^\d{9}$/;
    const nipc = control.value;
    if (regExpNipc.test(nipc)) {
      const firstCharacter = nipc.charAt(0);
      if (firstCharacter !== '8') {
        let checkDigit = parseInt(firstCharacter, 10) * 9;
        for (let i = 2; i <= 8; i++) {
          checkDigit += parseInt(nipc.charAt(i - 1), 10) * (10 - i);
        }
        checkDigit = (11 - (checkDigit % 11));
        if (checkDigit >= 10) {
          checkDigit = 0;
        }
        if (checkDigit === parseInt(nipc.charAt(8), 10)) {
          return null;
        }
      }
    }
    return { invalid_cif: true };
  }

  public validateCif = (control: FormControl): ValidationErrors | null => {
    const cif = control.value;
    if (!cif || cif === '') {
      return { required: 'required' };
    }
    if (cif.length !== 9) {
      return { invalid_cif: 'invalid_length' };
    }
    const letters = [ 'J', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I' ];
    const digits = cif.substr(1, cif.length - 2);
    const letter = cif.substr(0, 1);
    const controlDig = cif.substr(cif.length - 1);
    let sum = 0;
    let i: number;
    let digit: number;

    if (!letter.match(/[A-Z]/)) {
      return { invalid_cif: 'invalid_letter' };
    }

    for (i = 0; i < digits.length; ++i) {
      digit = parseInt(digits[ i ], 10);

      if (isNaN(digit)) {
        return { invalid_cif: 'invalid_length' };
      }

      if (i % 2 === 0) {
        digit *= 2;
        if (digit > 9) {
          digit = Math.floor(digit / 10) + (digit % 10);
        }

        sum += digit;
      } else {
        sum += digit;
      }
    }

    sum %= 10;
    if (sum !== 0) {
      digit = 10 - sum;
    } else {
      digit = sum;
    }

    if (letter.match(/[ABEH]/)) {
      if (String(digit) === controlDig) {
        return null;
      } else {
        return { invalid_cif: 'invalid_letter' };
      }
    }
    if (letter.match(/[NPQRSW]/)) {
      if (letters[ digit ] === controlDig) {
        return null;
      } else {
        return { invalid_cif: 'invalid_letter' };
      }
    }

    if (String(digit) === controlDig || letters[ digit ] === controlDig) {
      return null;
    } else {
      return { invalid_cif: 'invalid_cif' };
    }
  }

  public validateCNAE = (control: FormControl): ValidationErrors | null => {
    const cnae = control.value;
    const regExpCNAE = this.authService.userData.data.country === 'PT' ? /^[0-9]{5}$/ : /^[0-9]{4}$/;
    if (regExpCNAE.test(cnae)) {
      return null;
    } else {
      return { invalid_cnae: true };
    }
  }

  public validateMobile = (control: FormControl): ValidationErrors | null => {
    const mobile = control.value;
    const regExpMobile = /^[67]{1}[0-9]{8}$/;

    if (regExpMobile.test(mobile) !== true && mobile) {
      return { invalid_mobile: true };
    }

    return null;
  }

  public validateMobilePT = (control: FormControl): ValidationErrors | null => {
    const mobile = control.value;
    const regExpMobile = /^(([9][1|2|3|4|5|6][0-9]{7})|([2][1][0-9]{7})|([2][2][0-9]{7})|([2][3][1|2|3|4|5|6|8|9][0-9]{6})|([2][4][1|2|3|4|5|9][0-9]{6})|([2][5][1-9][0-9]{6})|([2][6][1|2|3|5|6|8|9][0-9]{6})|([2][7][1-9][0-9]{6})|([2][8][1|2|3|4|5|6|9][0-9]{6})|([2][9][1|2|5|6][0-9]{6}))$/;

    if (regExpMobile.test(mobile) !== true && mobile) {
      return { invalid_mobile: true };
    }

    return null;
  }
}
